import { useMemo, useEffect } from 'react';
import React from 'react';
import { LiveScoringQuestionHeader } from './LiveScoringQuestionHeader';
import { MatchStatsFeedSheet } from './MatchStatsFeedSheet';
import { renderQuestionScoring } from '@/utils/scoring/renderQuestionScoring';
import type { ScoringQuestionData } from '@/utils/scoring/types';
import type { Question } from '@/api/questions/getQuestions';
import type { MatchStatsResponse, TeamStat } from '@/api/feed/getFeedMatchStats';
import { useQuestionGradingResult } from './LiveScoringContext';
import { useSetManualScoringMutation } from '@/api/live-scoring/manualScoring';
import { useCheckQuestionOverridesQuery } from '@/api/live-scoring/checkQuestionHasOverrides';
import { useGetQuestionOverridesQuery } from '@/api/live-scoring/getQuestionOverrides';
import { VoidQuestionControl } from './VoidQuestionControl';

interface LiveScoringQuestionProps {
  question: Question;
  questionNumber: number;
  matchStats?: MatchStatsResponse;
}

export function LiveScoringQuestion({
  question,
  questionNumber,
  matchStats,
}: LiveScoringQuestionProps) {
  const isCustom = (question.question_type as string) === 'custom';
  const isVoided = Boolean(question.voided);
  // Custom questions are always manual scoring only (no feed, no auto-grading)
  const [manualScoring, setManualScoring] = React.useState(isCustom);
  const [feedStatsOpen, setFeedStatsOpen] = React.useState(false);
  const setManualScoringMutation = useSetManualScoringMutation(question.question_id);

  // Check if THIS SPECIFIC QUESTION has relevant overrides
  const { data: overrideCheck } = useCheckQuestionOverridesQuery(
    question.fixture_external_id ? question.question_id : undefined
  );

  // Fetch the actual override values
  const { data: questionOverrides } = useGetQuestionOverridesQuery(
    question.fixture_external_id ? question.question_id : null
  );

  // AUTO-ENABLE manual scoring when overrides detected for THIS question
  useEffect(() => {
    if (overrideCheck?.hasOverrides && !manualScoring && !isCustom) {
      setManualScoring(true);
      setManualScoringMutation.mutate(true);
    }
  }, [overrideCheck?.hasOverrides, manualScoring, isCustom, setManualScoringMutation]);

  // Determine question status (LIVE/FINAL) based on match status
  // Custom questions have no fixture - default to LIVE
  const questionStatus = useMemo(() => {
    if (isCustom) return 'LIVE';
    if (matchStats?.match.status === 'scheduled') {
      return 'SCHEDULED';
    }
    if (matchStats?.match.status === 'finished') {
      return 'FINAL';
    }
    if (matchStats?.match.status === 'live') {
      return 'LIVE';
    }
    return 'LIVE';
  }, [matchStats, isCustom]);

  // Read this question's live grading result from the shared per-pickem SSE
  // stream (one connection for the whole pickem) rather than opening a stream
  // per question. Only apply it under the same conditions the dedicated stream
  // used to be enabled: auto-graded (non-custom, non-manual) LIVE questions.
  const streamedResult = useQuestionGradingResult(question.question_id);
  const gradingResult =
    !isCustom && !manualScoring && questionStatus === 'LIVE' ? streamedResult : null;

  // Handle manual scoring toggle (not used for custom - always manual)
  const handleManualScoringChange = React.useCallback(
    async (enabled: boolean) => {
      if (isCustom) return; // Custom is always manual, no toggle
      setManualScoring(enabled);
      try {
        await setManualScoringMutation.mutateAsync(enabled);
      } catch (error) {
        console.error('Failed to set manual scoring:', error);
        setManualScoring(!enabled);
      }
    },
    [setManualScoringMutation, isCustom]
  );

  // Transform Question API type to ScoringQuestionData format
  // Update options with live grading results if available
  const scoringData: ScoringQuestionData = useMemo(() => {
    const options = (question.options || []).map(opt => {
      // Get relevant stats for this option based on question type
      let optionStats: unknown = null;
      if (matchStats) {
        // For team questions, show team stats
        if (question.question_type === 'team') {
          const teamId = opt.grading_data?.teamId?.toString();
          if (teamId) {
            const isHome = matchStats.match.homeTeamId === teamId;
            optionStats = isHome ? matchStats.teamStats.home : matchStats.teamStats.away;
          }
        }

        // For player questions, show player stats
        if (question.question_type === 'player') {
          const playerId = opt.grading_data?.playerId?.toString();
          if (playerId) {
            const allPlayerStats = [...matchStats.playerStats.home, ...matchStats.playerStats.away];
            const player = allPlayerStats.find(p => p.playerId === playerId);
            optionStats = player?.stats;
          }
        }

        // For prop questions, show player stats
        if (question.question_type === 'prop') {
          const playerId = opt.grading_data?.playerId?.toString();
          if (playerId) {
            const allPlayerStats = [...matchStats.playerStats.home, ...matchStats.playerStats.away];
            const player = allPlayerStats.find(p => p.playerId === playerId);
            optionStats = player?.stats;
          }
        }

        // For over/under questions (over_under, team_over_under, player_over_under), extract the relevant stat value
        if (
          question.question_type === 'over_under' ||
          question.question_type === 'team_over_under' ||
          question.question_type === 'player_over_under'
        ) {
          const playerId = opt.grading_data?.playerId?.toString();
          const teamId = opt.grading_data?.teamId?.toString();
          const statType = question.stat_type_code;
          if (playerId && statType) {
            const allPlayerStats = [...matchStats.playerStats.home, ...matchStats.playerStats.away];
            const player = allPlayerStats.find(p => p.playerId === playerId);
            if (player?.stats && statType in player.stats) {
              optionStats = { [statType]: player.stats[statType] };
            }
          } else if (teamId && statType) {
            // Find the stat for the specific team by checking if teamId matches home or away team
            const isHomeTeam = matchStats.match.homeTeamId === teamId;
            const isAwayTeam = matchStats.match.awayTeamId === teamId;

            let stat: TeamStat | undefined;
            if (isHomeTeam) {
              stat = matchStats.teamStats.home.find(t => t.statType === statType);
            } else if (isAwayTeam) {
              stat = matchStats.teamStats.away.find(t => t.statType === statType);
            }

            if (stat) {
              optionStats = { [statType]: stat.value };
            }
          }
        }
      }

      // Apply question-specific overrides from database (takes precedence over feed stats)
      if (questionOverrides?.overrides && questionOverrides.overrides.length > 0) {
        const playerId = opt.grading_data?.playerId?.toString();
        const teamId = opt.grading_data?.teamId?.toString();
        const statType = question.stat_type_code;

        // Find matching override for this option
        const override = questionOverrides.overrides.find(o => {
          if (playerId && o.playerId === playerId && o.statType === statType) return true;
          if (teamId && o.teamId === teamId && o.statType === statType) return true;
          return false;
        });

        if (override) {
          // Apply the override value
          if (statType) {
            optionStats = { [statType]: override.value };
          } else {
            optionStats = { value: override.value };
          }
        }
      }

      // Compute the grading_data to forward to the scoring adapters. Leader
      // templates read per-candidate stat values from grading_data.liveValue,
      // so we merge the live SSE value in here rather than mutating opt in place.
      let nextGradingData = opt.grading_data;

      // Leader questions are cross-fixture and have no single match feed, so the
      // adapter reads stat values from grading_data.liveValue/seasonValue. Merge
      // the live SSE value keyed by player/team id so each tick updates the rank.
      if (
        gradingResult?.actualValues &&
        (question.question_type === 'player_leader' || question.question_type === 'team_leader')
      ) {
        const entityId = (
          question.question_type === 'player_leader'
            ? opt.grading_data?.playerId
            : opt.grading_data?.teamId
        )?.toString();

        if (entityId && gradingResult.actualValues[entityId] !== undefined) {
          nextGradingData = {
            ...opt.grading_data,
            liveValue: gradingResult.actualValues[entityId],
          };
        }
      }

      // Update stats with actualValues from grading result if available
      // This ensures the displayed values match what was used for grading
      if (gradingResult?.actualValues) {
        const playerId = opt.grading_data?.playerId?.toString();
        const teamId = opt.grading_data?.teamId?.toString();
        const statType = question.stat_type_code;

        if (playerId && gradingResult.actualValues[playerId] !== undefined) {
          const actualValue = gradingResult.actualValues[playerId];
          if (statType) {
            // For over/under or prop questions with stat type
            optionStats = { [statType]: actualValue };
          } else {
            // For player selection questions, update the relevant stat
            // Try to preserve existing stats structure if possible
            if (optionStats && typeof optionStats === 'object' && !Array.isArray(optionStats)) {
              optionStats = { ...optionStats };
            } else {
              optionStats = { value: actualValue };
            }
          }
        } else if (teamId && gradingResult.actualValues[teamId] !== undefined) {
          const actualValue = gradingResult.actualValues[teamId];
          if (statType) {
            // For over/under questions with team stats
            optionStats = { [statType]: actualValue };
          } else {
            // For team selection questions
            if (optionStats && typeof optionStats === 'object' && !Array.isArray(optionStats)) {
              optionStats = { ...optionStats };
            } else {
              optionStats = { value: actualValue };
            }
          }
        }
      }

      const isCorrect = opt.is_correct || false;

      return {
        option_id: opt.option_id,
        option_text_en: opt.option_text_en || '',
        option_text_fr: opt.option_text_fr || '',
        option_image_en: opt.option_image_en || null,
        option_image_fr: opt.option_image_fr || null,
        third_image_en: opt.third_image_en || null,
        third_image_fr: opt.third_image_fr || null,
        is_correct: isCorrect,
        points: undefined, // Options don't have points in API response
        selectionCount: 0, // Placeholder - would come from API
        stats: optionStats,
        grading_data: nextGradingData,
      };
    });

    return {
      questionId: question.question_id,
      questionType: question.question_type || 'team',
      questionText: question.question_text_en || question.question_text_fr || '',
      questionSubtext: question.question_subtext_en || question.question_subtext_fr,
      questionImage: question.question_image_en || question.question_image_fr || null,
      options,
      language: 'en',
      totalPoints: question.points || 0,
      questionNumber,
      status: questionStatus,
      statTypeCode: question.stat_type_code ?? null,
      matchStats,
      correctValue: question.correct_value ?? null,
      manualScoring,
      // Leader questions (player_leader/team_leader) carry their stat config on
      // question_metadata.leaderboard rather than stat_type_code. Pass it
      // through so the leader scoring template can label the ranked column.
      leaderboard: (
        question.question_metadata as
          | { leaderboard?: ScoringQuestionData['leaderboard'] }
          | null
          | undefined
      )?.leaderboard,
    };
  }, [
    question,
    questionNumber,
    questionStatus,
    matchStats,
    gradingResult,
    manualScoring,
    questionOverrides,
  ]);

  const handleMarkCorrect = (_optionId: string, _isCorrect: boolean) => {
    // TODO: Implement mark correct functionality
  };

  const handleSave = (_optionId: string) => {
    // TODO: Implement save functionality
  };

  const handleCancel = (_optionId: string) => {
    // TODO: Implement cancel functionality
  };

  const handleStatChange = (_optionId: string, _statValue: number) => {
    // TODO: Implement stat change functionality
  };

  return (
    <>
      <div className="border rounded-lg p-4 space-y-3">
        {/* Question Header with Scoring Info */}
        <LiveScoringQuestionHeader
          question={question}
          questionNumber={questionNumber}
          status={questionStatus}
          points={question.points || 0}
          manualScoring={manualScoring}
          onManualScoringChange={handleManualScoringChange}
          hasOverrides={overrideCheck?.hasOverrides || false}
          onViewFeedStats={() => setFeedStatsOpen(true)}
        />

        {/* Void / un-void control (mark all options correct — "no contest").
            Hidden for tiebreakers: they have no options, so a void is not supported. */}
        {question.question_type !== 'tiebreaker' && (
          <div className="flex justify-end">
            <VoidQuestionControl
              questionId={question.question_id}
              voided={isVoided}
              voidedReason={question.voided_reason}
            />
          </div>
        )}

        {/* Show override notice if applicable (moot once voided) */}
        {!isVoided && overrideCheck?.hasOverrides && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <span className="text-amber-900 dark:text-amber-200 font-semibold">
                  🔧 Manual Stats Active
                </span>
                <span className="text-sm text-amber-700 dark:text-amber-300 ml-2">
                  {overrideCheck.overrideCount} stat{overrideCheck.overrideCount > 1 ? 's' : ''}{' '}
                  overridden for this question
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Question Content - Using new scoring renderer */}
        <div className="relative">
          {renderQuestionScoring(scoringData, {
            questionId: question.question_id,
            fixtureId: question.fixture_external_id || undefined,
            onMarkCorrect: handleMarkCorrect,
            onSave: handleSave,
            onCancel: handleCancel,
            language: 'en',
            manualScoring,
            matchStats,
            onStatChange: handleStatChange,
            onOverrideSuccess: () => {
              // Refetch match stats to get updated values after override
              // This will trigger a re-render with new stat values
              if (question.fixture_external_id) {
                // The query will automatically refetch if needed
                // For now, we can rely on the mutation's onSuccess to handle updates
              }
            },
          })}
        </div>
      </div>

      <MatchStatsFeedSheet
        match={question.fixture_external_id ? { id: question.fixture_external_id } : null}
        open={feedStatsOpen}
        onOpenChange={setFeedStatsOpen}
      />
    </>
  );
}
